import { getEnv } from "../config/env";
import { ENVIRONMENTS } from "../config/environments";
import segmentsApiService from "./SegmentsApiService";
import { getRecommendedThreshold } from "../utils";

// Terms reach this service with `score` already converted to a cosine
// distance; fall back to the sheet default (Segments -> Strong Match).
const SEGMENT_DEFAULT_THRESHOLD = getRecommendedThreshold("SEGMENTS");

const SEARCH_QUERY = `
  query SearchSegments($input: SegmentSearchInput!) {
    searchSegments(input: $input) {
      entries {
        key
        response {
          totalCount
          data {
            score
            compositeScore
            segmentVector {
              segmentId
              originalSegmentPath
              audienceType
              categoryL1
              categoryL2
              attributeName
              attributeValue
              valueQualifier
              isAbm
              isLeaf
              isNavOnly
              isArchived
              sellable
              hasMeta
              reach
              segmentName
              description
              accountId
              accountName
              allowedAccounts
              bqLocation
              externalId
              fullName
              type
              subType
              segmentGroupId
              category
              subCategory
              source
              providerId
              status
              propensity
              allowedCountries
              path
              segmentCategory
              segmentType
              segmentSource
              segmentSourceDescription
              priceCpm
              pricePom
              sourceProvidedCount
              sourceMatchedCount
              gcsFilePath
              dateAdded
              expirationDate
              isDeletedInSource
            }
            segmentMetrics {
              reach
              uniqueUsers
              requests
              soldImpressions
              avgCpm
              minCpm
              maxCpm
              weightedImpressions
              weightedViewability
              weightedClickThroughRate
              weightedVideoThroughRate
              weightedBidCpm
              weightedBidGuidance
              advertiserSpend
              avgSpendPerUniqueUser
              impressionFrequency
              impressionFrequencyPerDay
              reportMonths
              latestStatus
              weightedClicks
              weightedFills
              weightedStartFirstOccurrence
              weightedCompleteFirstOccurrence
              weightedViewables
              weightedSpendInUsd
              uniqueUsersWithRequests
              uniqueIpsWithRequests
              uniqueUsersWithImpressions
              cookies
              mobileDevices
              tabletDevices
              connectedTvDevices
              weightedOxWinRate
              weightedPubWinRate
              weightedNonZeroBids
              weightedNonZeroBidsValue
              households
            }
          }
          cumulativeData {
            key
            noOfSegments
            reach
            segmentIds
            clickThroughRate
            videoCompletionRate
            viewability
            bidGuidance
            relevance
            cpmMin
            cpmMax
          }
        }
      }
    }
  }
`;

const UNIFIED_SET_QUERY = `
  query BuildUnifiedAudienceSet($input: UnifiedSetInput!) {
    buildUnifiedAudienceSet(input: $input) {
      segments {
        score
        compositeScore
        segmentVector {
          segmentId
          originalSegmentPath
          audienceType
          categoryL1
          categoryL2
          attributeName
          attributeValue
          valueQualifier
          isAbm
          isLeaf
          isNavOnly
          isArchived
          sellable
          hasMeta
          reach
          segmentName
          description
          accountId
          accountName
          allowedAccounts
          bqLocation
          externalId
          fullName
          type
          subType
          segmentGroupId
          category
          subCategory
          source
          providerId
          status
          propensity
          allowedCountries
          path
          segmentCategory
          segmentType
          segmentSource
          segmentSourceDescription
          priceCpm
          pricePom
          sourceProvidedCount
          sourceMatchedCount
          gcsFilePath
          dateAdded
          expirationDate
          isDeletedInSource
        }
        segmentMetrics {
          reach
          uniqueUsers
          requests
          soldImpressions
          avgCpm
          minCpm
          maxCpm
          weightedImpressions
          weightedViewability
          weightedClickThroughRate
          weightedVideoThroughRate
          weightedBidCpm
          weightedBidGuidance
          advertiserSpend
          avgSpendPerUniqueUser
          impressionFrequency
          impressionFrequencyPerDay
          reportMonths
          latestStatus
          weightedClicks
          weightedFills
          weightedStartFirstOccurrence
          weightedCompleteFirstOccurrence
          weightedViewables
          weightedSpendInUsd
          uniqueUsersWithRequests
          uniqueIpsWithRequests
          uniqueUsersWithImpressions
          cookies
          mobileDevices
          tabletDevices
          connectedTvDevices
          weightedOxWinRate
          weightedPubWinRate
          weightedNonZeroBids
          weightedNonZeroBidsValue
          households
        }
      }
      segmentSets {
        type
        scoringConfig {
          setType
          formula
          relevanceWeight
        }
        segmentScores {
          segmentId
          score
          matchedKeyword
        }
        metrics {
          reach
          totalRequests
          totalImpressions
          viewability
          bidGuidance
          clickThroughRate
          videoCompletionRate
          avgCpm
          minCpm
          maxCpm
          mobileDevices
          connectedTvDevices
          activeIps
          totalCookies
          advertiserSpend
          avgSpendPerUniqueUser
          totalClicks
          totalFills
          relevance
        }
        reachMetadata {
          reachGoal
          reachGoalMet
          effectiveReach
          reachMethod
          groupWeights {
            key
            value
          }
          groupAvgReaches {
            key
            value
          }
        }
      }
      groupLogicResolved {
        operator
        key
      }
    }
  }
`;

const NLP_SEARCH_QUERY = `
  query SearchSegmentsByText($input: NlpSegmentSearchInput!) {
    searchSegmentsByText(input: $input) {
    llmParsed
    queryType
    parsedQuery {
      segmentSearchParams {
        query {
          query
          threshold
        }
        excludeQuery {
          query
          threshold
        }
        filters {
          segmentVector {
            priceCpm
          }
          segmentMetrics {
            reach
            soldImpressions
            avgCpm
            weightedClickThroughRate
            weightedVideoThroughRate
            impressionFrequencyPerDay
          }
          country
          region
          demographicFilters {
            gender
            ageRange {
              start
              end
            }
            locationType
            location
          }
          categoryFilters {
            categoryL2
            attributeName
            numericMin
            numericMax
          }
        }
        sortBy
        sortDirection
        limit
        startIndex
        lookbackWindowInDays
        includeAudience
      }
      unifiedSetParams {
        segmentSearchInput {
          query {
            query
            threshold
          }
          excludeQuery {
            query
            threshold
          }
          filters {
            segmentVector {
              priceCpm
            }
            segmentMetrics {
              reach
              soldImpressions
              avgCpm
              weightedClickThroughRate
              weightedVideoThroughRate
              impressionFrequencyPerDay
            }
            country
            region
            demographicFilters {
              gender
              ageRange {
                start
                end
              }
              locationType
              location
            }
            categoryFilters {
              categoryL2
              attributeName
              numericMin
              numericMax
            }
          }
          sortBy
          sortDirection
          limit
          startIndex
          lookbackWindowInDays
          includeAudience
        }
        unifiedSetType
        groupLogic {
          operator
          key
          children {
            operator
            key
            children {
              operator
              key
            }
          }
        }
        reachConfig {
          reachGoal
        }
        topNSegments
        minSegments
        oxsWeight
      }
    }
  }
}
`;

const ENRICH_ESTIMATE_QUERY = `
  query EnrichEstimate($input: EnrichEstimateInput!) {
    enrichEstimate(input: $input) {
      reachMethod
      setMetrics {
        reach
        totalImpressions
        totalRequests
        bidGuidance
        avgCpm
        minCpm
        maxCpm
        viewability
        clickThroughRate
        videoCompletionRate
        mobileDevices
        connectedTvDevices
        advertiserSpend
        avgSpendPerUniqueUser
      }
    }
  }
`;

class GraphQLSegmentsApiService {
  constructor() {
    const env = getEnv();
    this.proxyUrl = (env.REACT_APP_PROXY || "/api").replace(/\/$/, "");
    this.endpoint = env.REACT_APP_GRAPHQL_URL || `${this.proxyUrl.replace(/\/api$/, "")}/graphql`;
    // Restore environment selected on the login page (persisted across refreshes).
    // Only honour the saved value if it matches a known environment — stale/old URLs are discarded.
    try {
      const saved = localStorage.getItem("wissp_env_proxy");
      const knownProxies = ENVIRONMENTS.map((e) => e.proxy.replace(/\/$/, ""));
      if (saved && knownProxies.includes(saved.replace(/\/$/, ""))) {
        this.proxyUrl = saved.replace(/\/$/, "");
        this.endpoint = `${this.proxyUrl.replace(/\/api$/, "")}/graphql`;
      } else if (saved) {
        localStorage.removeItem("wissp_env_proxy");
      }
    } catch (_) {}
    console.log("[GraphQLSegmentsApiService] endpoint:", this.endpoint);
  }

  setProxyUrl(proxyBaseUrl) {
    this.proxyUrl = proxyBaseUrl.replace(/\/$/, "");
    this.endpoint = `${this.proxyUrl.replace(/\/api$/, "")}/graphql`;
    try { localStorage.setItem("wissp_env_proxy", this.proxyUrl); } catch (_) {}
    console.log("[GraphQLSegmentsApiService] environment switched →", this.endpoint);
  }

  isAuthenticated() {
    return segmentsApiService.isAuthenticated();
  }

  redirectToLogin() {
    return segmentsApiService.redirectToLogin();
  }

  getFilterOptions(signal = null) {
    return segmentsApiService.getFilterOptions(signal);
  }

  _getAuthHeaders() {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    const token = segmentsApiService.getSharedAuthToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }

  _buildInput(searchTerms, excludeTerms, filters, options) {
    const sv = filters.segmentVector || {};
    const sm = filters.segmentMetrics || {};
    const df = filters.demographicFilters || {};
    const cf = Array.isArray(filters.categoryFilters)
      ? filters.categoryFilters.filter((f) => f.categoryL2)
      : [];

    const pickArray = (v) => (Array.isArray(v) && v.length > 0 ? v : undefined);
    const pickNum = (v) => (v != null && v !== 0 ? v : undefined);

    const segmentVectorInput = {
      audienceType: pickArray(sv.audienceType),
      categoryL1: pickArray(sv.categoryL1),
      categoryL2: pickArray(sv.categoryL2),
      attributeName: pickArray(sv.attributeName),
      attributeValue: pickArray(sv.attributeValue),
      valueQualifier: pickArray(sv.valueQualifier),
      accountName: pickArray(sv.accountName),
      type: pickArray(sv.type),
      subType: pickArray(sv.subType),
      category: pickArray(sv.category),
      subCategory: pickArray(sv.subCategory),
      source: pickArray(sv.source),
      providerId: pickArray(sv.providerId),
      allowedCountries: pickArray(sv.allowedCountries),
      isAbm: sv.isAbm != null ? sv.isAbm : undefined,
      isLeaf: sv.isLeaf != null ? sv.isLeaf : undefined,
      isNavOnly: sv.isNavOnly != null ? sv.isNavOnly : undefined,
      isArchived: sv.isArchived != null ? sv.isArchived : undefined,
      sellable: sv.sellable != null ? sv.sellable : undefined,
      hasMeta: sv.hasMeta != null ? sv.hasMeta : undefined,
      reach: pickNum(sv.reach),
      priceCpm: pickNum(sv.priceCpm),
    };

    const segmentMetricsInput = {
      reach: pickNum(sm.reach),
      avgCpm: pickNum(sm.avgEcpm),
      requests: pickNum(sm.requests),
      soldImpressions: pickNum(sm.soldImpressions),
      uniqueUsers: pickNum(sm.uniqueUsers),
      weightedImpressions: pickNum(sm.weightedImpressions),
      weightedClickThroughRate: pickNum(sm.weightedClickThroughRate),
      weightedVideoThroughRate: pickNum(sm.weightedVideoThroughRate),
      impressionFrequencyPerDay: pickNum(sm.impressionFrequencyPerDay),
    };

    const hasMetrics = Object.values(segmentMetricsInput).some((v) => v != null);

    const ageStart = parseInt(df.ageRange?.start);
    const ageEnd = parseInt(df.ageRange?.end);
    const hasAgeRange = !isNaN(ageStart) && !isNaN(ageEnd);
    const hasLocation = Array.isArray(df.location) && df.location.length > 0;
    const hasGender = !!df.gender;

    const demographicFilters =
      hasAgeRange || hasLocation || hasGender
        ? {
          ageRange: hasAgeRange
            ? { start: ageStart, end: ageEnd }
            : undefined,
          location: pickArray(df.location),
          locationType: df.locationType ? df.locationType.toUpperCase() : undefined,
          gender: df.gender || undefined,
        }
        : undefined;

    const cumulativeThresholds = filters.cumulativeThresholds || {};
    const cumulativeDataFilters = {
      cumulativeSetsFilters: [
        {
          key: "Unique User Optimised",
          sortBy: "score",
          isASC: true,
          filterBy: "uniqueUsers",
          threshold: cumulativeThresholds.uniqueUsers || 1500000000,
        },
        {
          key: "Composite Score Optimised",
          sortBy: "compositeScore",
          isASC: false,
          filterBy: "calculatedReach",
          threshold: cumulativeThresholds.reach_composite || 38000000,
        },
        {
          key: "Relevance Optimised",
          sortBy: "score",
          isASC: true,
          filterBy: "calculatedReach",
          threshold: cumulativeThresholds.reach_relevance || 38000000,
        },
        {
          key: "Impressions Optimised",
          sortBy: "score",
          isASC: true,
          filterBy: "weightedImpressions",
          threshold: cumulativeThresholds.weightedImpressions || 700000,
        },
      ],
    };

    return {
      query: searchTerms
        .filter((t) => t.text || t.query)
        .map((t) => ({ query: t.text || t.query, threshold: t.score ?? t.threshold ?? SEGMENT_DEFAULT_THRESHOLD })),
      excludeQuery: excludeTerms
        .filter((t) => t.text || t.query)
        .map((t) => ({ query: t.text || t.query, threshold: t.score ?? t.threshold ?? SEGMENT_DEFAULT_THRESHOLD })),
      filters: {
        segmentVector: segmentVectorInput,
        segmentMetrics: hasMetrics ? segmentMetricsInput : undefined,
        cumulativeDataFilters,
        demographicFilters,
        categoryFilters: cf.length > 0 ? cf : undefined,
        country: pickArray(filters.country),
        region: filters.region || undefined,
      },
      sortBy: options.sortBy || filters.sortBy,
      sortDirection: options.view || filters.view || "DESC",
      startIndex: options.startIndex || filters.startIndex || 0,
      limit: options.noOfResult || filters.noOfResult || 250,
      lookbackWindowInDays: options.noOfDays || filters.noOfDays || 0,
      includeAudience: options.isAudienceRequire ?? filters.isAudienceRequire ?? false,
    };
  }

  async performSegmentSearch(searchTerms = [], excludeTerms = [], filters = {}, options = {}) {
    try {
      const finalSearchTerms = options.searchTerms || searchTerms;
      const finalExcludeTerms = options.excludeTerms || excludeTerms;

      const input = this._buildInput(finalSearchTerms, finalExcludeTerms, filters, options);

      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: this._getAuthHeaders(),
        body: JSON.stringify({ query: SEARCH_QUERY, variables: { input } }),
        signal: options.signal || AbortSignal.timeout(300000),
      });

      if (response.status === 401 || response.status === 403) {
        return { success: false, error: "Authentication failed", status: response.status };
      }

      if (!response.ok) {
        const text = await response.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const json = await response.json();

      if (json.errors?.length) {
        throw new Error(json.errors.map((e) => e.message).join("; "));
      }

      const entries = json.data?.searchSegments?.entries ?? [];

      // Transform GraphQL entries into the shape useSegments expects:
      // { [key]: { data: [...], totalCount: N } }
      const transformed = {};
      entries.forEach((entry) => {
        const cumulativeDataRaw = entry.response?.cumulativeData ?? [];
        const cumulativeData = {};
        cumulativeDataRaw.forEach((item) => {
          if (item?.key) cumulativeData[item.key] = item;
        });
        transformed[entry.key] = {
          data: entry.response?.data ?? [],
          totalCount: entry.response?.totalCount ?? 0,
          cumulativeData,
        };
      });

      return { success: true, data: transformed };
    } catch (error) {
      if (error.name === "AbortError") throw error;
      return { success: false, error: error.message, data: null };
    }
  }

  async performUnifiedSetSearch(searchTerms = [], excludeTerms = [], filters = {}, options = {}) {
    try {
      const finalSearchTerms = options.searchTerms || searchTerms;
      const finalExcludeTerms = options.excludeTerms || excludeTerms;

      const searchInput = this._buildInput(finalSearchTerms, finalExcludeTerms, filters, options);

      const unifiedSetType = options.unifiedSetType || "TOP_N";
      const unifiedInput = {
        searchInput,
        unifiedSetType,
        ...(options.minSegments != null && { minSegments: options.minSegments }),
        ...(options.andMultiplier != null && { andMultiplier: options.andMultiplier }),
        ...(options.highestScaleRelevanceWeight != null && { highestScaleRelevanceWeight: options.highestScaleRelevanceWeight }),
        ...(options.bestPerformanceRelevanceWeight != null && { bestPerformanceRelevanceWeight: options.bestPerformanceRelevanceWeight }),
        ...(options.oxsWeight != null && { oxsWeight: options.oxsWeight }),
        ...(options.llmRelevanceGateEnabled && { llmRelevanceGateEnabled: true }),
        ...(unifiedSetType === "REACH_BASED"
          ? {
            reachConfig: {
              reachGoal: options.reachGoal || 50000000,
              ...(options.segmentRetentionRate != null && { segmentRetentionRate: options.segmentRetentionRate }),
              ...(options.maxOptimisationRounds != null && { maxOptimisationRounds: options.maxOptimisationRounds }),
              ...(options.highestScaleStartSize != null && { highestScaleStartSize: options.highestScaleStartSize }),
              ...(options.bestPerformanceStartSize != null && { bestPerformanceStartSize: options.bestPerformanceStartSize }),
              ...(options.mostRelevantStartSize != null && { mostRelevantStartSize: options.mostRelevantStartSize }),
            },
          }
          : { topNSegments: options.topNSegments || 10 }),
      };

      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: this._getAuthHeaders(),
        body: JSON.stringify({ query: UNIFIED_SET_QUERY, variables: { input: unifiedInput } }),
        signal: options.signal || AbortSignal.timeout(300000),
      });

      if (response.status === 401 || response.status === 403) {
        return { success: false, error: "Authentication failed", status: response.status };
      }

      if (!response.ok) {
        const text = await response.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const json = await response.json();

      if (json.errors?.length) {
        throw new Error(json.errors.map((e) => e.message).join("; "));
      }

      const result = json.data?.buildUnifiedAudienceSet;

      const segmentMap = {};
      (result?.segments || []).forEach((seg) => {
        if (seg.segmentVector?.segmentId) {
          segmentMap[seg.segmentVector.segmentId] = seg;
        }
      });

      return {
        success: true,
        data: {
          segmentSets: result?.segmentSets || [],
          segments: result?.segments || [],
          segmentMap,
          groupLogicResolved: result?.groupLogicResolved,
        },
      };
    } catch (error) {
      if (error.name === "AbortError") throw error;
      return { success: false, error: error.message, data: null };
    }
  }
  async searchSegmentsByText(text, queryType = "SEGMENT_SEARCH") {
    try {
      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: this._getAuthHeaders(),
        body: JSON.stringify({
          query: NLP_SEARCH_QUERY,
          variables: { input: { text, queryType } },
        }),
        signal: AbortSignal.timeout(300000),
      });

      if (response.status === 401 || response.status === 403) {
        return { success: false, error: "Authentication failed", status: response.status };
      }

      if (!response.ok) {
        const text = await response.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const json = await response.json();

      if (json.errors?.length) {
        throw new Error(json.errors.map((e) => e.message).join("; "));
      }

      const result = json.data?.searchSegmentsByText;
      return { success: true, data: result };
    } catch (error) {
      if (error.name === "AbortError") throw error;
      return { success: false, error: error.message, data: null };
    }
  }

  async enrichEstimate(scores, segmentMap) {
    const segmentMetrics = scores
      .filter(({ segmentId }) => segmentMap[segmentId]?.segmentMetrics)
      .map(({ segmentId }) => {
        const sm = segmentMap[segmentId].segmentMetrics;
        return {
          id: segmentId,
          reach: sm.reach || 0,
          weightedImpressions: sm.weightedImpressions || 0,
          weightedImpressionsWithClickMacro: sm.weightedImpressionsWithClickMacro || 0,
          weightedClicks: sm.weightedClicks || 0,
          weightedFills: sm.weightedFills || 0,
          weightedCompleteFirstOccurrence: sm.weightedCompleteFirstOccurrence || 0,
          weightedStartFirstOccurrence: sm.weightedStartFirstOccurrence || 0,
          weightedViewables: sm.weightedViewables || 0,
          weightedViewabilityMeasures: sm.weightedViewabilityMeasures || 0,
          weightedSpendInUsd: sm.weightedSpendInUsd || 0,
          cookies: sm.cookies || 0,
          requests: sm.requests || 0,
          uniqueIpsWithRequests: sm.uniqueIpsWithRequests || 0,
          mobileDevices: sm.mobileDevices || 0,
          connectedTvDevices: sm.connectedTvDevices || 0,
          minCpm: sm.minCpm || 0,
          maxCpm: sm.maxCpm || 0,
          avgCpm: sm.avgCpm || 0,
          avgSpendPerUniqueUser: sm.avgSpendPerUniqueUser || 0,
        };
      });

    if (!segmentMetrics.length) return null;

    const normalized_definition = {
      includes: {
        operator: "UNION",
        groups: segmentMetrics.map(({ id }) => ({
          operator: "INTERSECT",
          predefined: { operator: "UNION", segments: [{ id }] },
        })),
      },
    };

    const response = await fetch(this.endpoint, {
      method: "POST",
      headers: this._getAuthHeaders(),
      body: JSON.stringify({
        query: ENRICH_ESTIMATE_QUERY,
        variables: { input: { segmentMetrics, normalized_definition } },
      }),
      signal: AbortSignal.timeout(30000),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    if (json.errors?.length) throw new Error(json.errors.map((e) => e.message).join("; "));
    const ee = json.data?.enrichEstimate;
    if (!ee) return null;
    return { ...ee.setMetrics, reachMethod: ee.reachMethod };
  }

}

const graphQLSegmentsApiService = new GraphQLSegmentsApiService();
export default graphQLSegmentsApiService;
